/**
 * ROI Calculation Hook
 * Custom hook for educational ROI calculations with API integration
 * Epic 18: Enhanced marketing ROI calculators
 */

import { useState, useCallback } from 'react';

type CalculatorType =
  | 'student-performance'
  | 'teacher-productivity'
  | 'cost-savings'
  | 'learning-outcomes'
  | 'technology-investment';

interface ROIInputs {
  // Student Performance Inputs
  currentStudentCount?: number;
  currentGradingHours?: number;
  averageTeacherSalary?: number;
  targetImprovement?: number;
  automationSavings?: number;

  // Teacher Productivity Inputs
  teacherCount?: number;
  avgClassSize?: number;
  gradingTimePerStudent?: number;
  lessonPlanningHours?: number;
  automatedGradingPercent?: number;
  planningEfficiency?: number;

  // Cost Savings Inputs
  annualTechnologyBudget?: number;
  textbookCostPerStudent?: number;
  professionalDevelopmentCost?: number;
  digitalResourceSavings?: number;
  operationalSavings?: number;

  // Learning Outcomes Inputs
  currentTestScores?: number;
  currentEngagement?: number;
  studentCount?: number;
  engagementIncrease?: number;

  // Technology Investment Inputs
  implementationCost?: number;
  annualLicenseCost?: number;
  trainingCost?: number;
  efficiencyGains?: number;
  costReductions?: number;
}

interface ROIResults {
  annualSavings: number;
  threeYearROI: number;
  paybackPeriod: number;
  productivityGains: number;
  studentOutcomeImprovement: number;
  costPerStudent: number;
  breakdownByCategory: {
    [key: string]: number;
  };
}

interface UseROICalculationReturn {
  calculateROI: (calculatorType: CalculatorType, inputs: ROIInputs) => ROIResults | null;
  calculateROIAsync: (calculatorType: CalculatorType, inputs: ROIInputs) => Promise<ROIResults>;
  calculateROIRealtime: (calculatorType: CalculatorType, inputs: ROIInputs) => Promise<ROIResults>;
  loading: boolean;
  error: string | null;
}

export const useROICalculation = (): UseROICalculationReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Client-side ROI calculation for immediate results
  const calculateROI = useCallback((calculatorType: CalculatorType, inputs: ROIInputs): ROIResults | null => {
    try {
      setError(null);

      switch (calculatorType) {
        case 'student-performance':
          return calculateStudentPerformanceROI(inputs);
        case 'teacher-productivity':
          return calculateTeacherProductivityROI(inputs);
        case 'cost-savings':
          return calculateCostSavingsROI(inputs);
        case 'learning-outcomes':
          return calculateLearningOutcomesROI(inputs);
        case 'technology-investment':
          return calculateTechnologyInvestmentROI(inputs);
        default:
          throw new Error('Invalid calculator type');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Calculation failed');
      return null;
    }
  }, []);

  // Server-side ROI calculation with enhanced v1 API and <10ms performance
  const calculateROIAsync = useCallback(
    async (calculatorType: CalculatorType, inputs: ROIInputs): Promise<ROIResults> => {
      setLoading(true);
      setError(null);

      try {
        // Map calculator types to v1 API endpoints
        const apiEndpoint =
          calculatorType === 'teacher-productivity'
            ? '/api/v1/calculators/calculate/time-savings'
            : '/api/v1/calculators/calculate/roi';

        // Transform inputs to match v1 API format
        const transformedInputs = transformInputsForV1API(calculatorType, inputs);

        const response = await fetch(apiEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(transformedInputs),
        });

        if (!response.ok) {
          throw new Error(`Server error: ${response.status}`);
        }

        const data = await response.json();

        // Transform v1 API results back to component format
        return transformV1ResultsToROIResults(data.data || data.results);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Server calculation failed';
        setError(errorMessage);

        // Fallback to client-side calculation
        const fallbackResult = calculateROI(calculatorType, inputs);
        if (fallbackResult) {
          return fallbackResult;
        }
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [calculateROI]
  );

  // Real-time calculation for progressive updates (Epic 18 requirement)
  const calculateROIRealtime = useCallback(
    async (calculatorType: CalculatorType, inputs: ROIInputs): Promise<ROIResults> => {
      // For real-time calculations, use client-side first for immediate feedback
      const clientResult = calculateROI(calculatorType, inputs);

      if (clientResult) {
        // Start background server calculation for enhanced accuracy
        calculateROIAsync(calculatorType, inputs).catch(err => {
          console.warn('Background server calculation failed:', err);
        });

        return clientResult;
      }

      // Fallback to server calculation if client calculation fails
      return calculateROIAsync(calculatorType, inputs);
    },
    [calculateROI, calculateROIAsync]
  );

  return {
    calculateROI,
    calculateROIAsync,
    calculateROIRealtime,
    loading,
    error,
  };
};

// Transform legacy inputs to v1 API format
function transformInputsForV1API(calculatorType: CalculatorType, inputs: ROIInputs): any {
  switch (calculatorType) {
    case 'teacher-productivity':
      return {
        teacherCount: inputs.teacherCount || 10,
        avgClassSize: inputs.avgClassSize || 25,
        gradingHoursPerWeek: ((inputs.gradingTimePerStudent || 10) * (inputs.avgClassSize || 25)) / 60,
        lessonPlanningHours: inputs.lessonPlanningHours || 8,
        administrativeHours: 5, // Default administrative hours
        averageTeacherSalary: 55000, // Default salary
        automationLevel: inputs.automatedGradingPercent || 70,
      };

    default: // ROI calculations
      return {
        institutionSize: inputs.currentStudentCount || inputs.studentCount || 100,
        currentCostPerStudent: inputs.textbookCostPerStudent || 500,
        teacherHours: inputs.currentGradingHours || 10,
        teacherSalary: inputs.averageTeacherSalary || 55000,
        currentTestScores: inputs.currentTestScores || 75,
        currentEngagement: inputs.currentEngagement || 65,
        targetImprovement: inputs.targetImprovement || 15,
        implementationTimeframe: 12, // Default 12 months
        techAdoptionRate: inputs.automationSavings || inputs.efficiencyGains || 60,
      };
  }
}

// Transform v1 API results to component format
function transformV1ResultsToROIResults(v1Results: any): ROIResults {
  if (v1Results.weeklyTimeSaved !== undefined) {
    // Time savings results
    return {
      annualSavings: Math.round(v1Results.monetaryValue || 0),
      threeYearROI: Math.round((((v1Results.monetaryValue || 0) * 3) / 50000) * 100), // Estimated ROI
      paybackPeriod: Math.ceil(50000 / ((v1Results.monetaryValue || 1) / 12)), // Estimated payback
      productivityGains: Math.round(v1Results.productivityIncrease || 0),
      studentOutcomeImprovement: 20, // Estimated
      costPerStudent: Math.round((v1Results.monetaryValue || 0) / 500), // Estimated
      breakdownByCategory: {
        'Time Savings Value': Math.round(v1Results.monetaryValue || 0),
        'Productivity Gains': Math.round((v1Results.monetaryValue || 0) * 0.6),
        'Additional Capacity': Math.round((v1Results.monetaryValue || 0) * 0.4),
      },
    };
  } else {
    // ROI calculation results
    return {
      annualSavings: Math.round(v1Results.annualSavings || 0),
      threeYearROI: Math.round(v1Results.threeYearROI || 0),
      paybackPeriod: Math.ceil(v1Results.paybackPeriod || 12),
      productivityGains: Math.round(v1Results.timeSavingsHours || 60),
      studentOutcomeImprovement: Math.round(v1Results.studentOutcomeImprovement || 15),
      costPerStudent: Math.round(v1Results.costPerStudent || 0),
      breakdownByCategory: {
        'Annual Savings': Math.round(v1Results.annualSavings || 0),
        'Cost Reduction': Math.round(v1Results.costSavings || 0),
        'Outcome Value': Math.round((v1Results.annualSavings || 0) * 0.4),
        'Efficiency Gains': Math.round((v1Results.annualSavings || 0) * 0.3),
      },
    };
  }
}

// Student Performance ROI Calculation
function calculateStudentPerformanceROI(inputs: ROIInputs): ROIResults {
  const {
    currentStudentCount = 100,
    currentGradingHours = 10,
    averageTeacherSalary = 55000,
    targetImprovement = 15,
    automationSavings = 60,
  } = inputs;

  // Calculate grading time savings
  const hourlyRate = averageTeacherSalary / (40 * 52); // Weekly hours * weeks per year
  const gradingTimeSavings = currentGradingHours * (automationSavings / 100) * hourlyRate * 52;

  // Calculate student outcome value (estimated based on improved performance)
  const outcomeValue = currentStudentCount * (targetImprovement / 100) * 500; // $500 per percentage point improvement

  // Total annual savings
  const annualSavings = gradingTimeSavings + outcomeValue;

  // Implementation cost (estimated)
  const implementationCost = currentStudentCount * 50; // $50 per student

  // Calculate ROI metrics
  const threeYearROI = ((annualSavings * 3 - implementationCost) / implementationCost) * 100;
  const paybackPeriod = Math.ceil(implementationCost / (annualSavings / 12));
  const productivityGains = automationSavings;
  const studentOutcomeImprovement = targetImprovement;
  const costPerStudent = annualSavings / currentStudentCount;

  return {
    annualSavings: Math.round(annualSavings),
    threeYearROI: Math.round(threeYearROI),
    paybackPeriod,
    productivityGains,
    studentOutcomeImprovement,
    costPerStudent: Math.round(costPerStudent),
    breakdownByCategory: {
      'Grading Time Savings': Math.round(gradingTimeSavings),
      'Student Outcome Value': Math.round(outcomeValue),
      'Administrative Efficiency': Math.round(annualSavings * 0.1),
    },
  };
}

// Teacher Productivity ROI Calculation
function calculateTeacherProductivityROI(inputs: ROIInputs): ROIResults {
  const {
    teacherCount = 10,
    avgClassSize = 25,
    gradingTimePerStudent = 10,
    lessonPlanningHours = 8,
    automatedGradingPercent = 70,
    planningEfficiency = 40,
  } = inputs;

  const averageTeacherSalary = 55000;
  const hourlyRate = averageTeacherSalary / (40 * 52);

  // Calculate grading time savings
  const weeklyGradingTime = (avgClassSize * gradingTimePerStudent) / 60; // Convert to hours
  const gradingTimeSavings = teacherCount * weeklyGradingTime * (automatedGradingPercent / 100) * hourlyRate * 52;

  // Calculate planning time savings
  const planningTimeSavings = teacherCount * lessonPlanningHours * (planningEfficiency / 100) * hourlyRate * 52;

  // Total annual savings
  const annualSavings = gradingTimeSavings + planningTimeSavings;

  // Implementation cost
  const implementationCost = teacherCount * 2000; // $2000 per teacher

  // Calculate ROI metrics
  const threeYearROI = ((annualSavings * 3 - implementationCost) / implementationCost) * 100;
  const paybackPeriod = Math.ceil(implementationCost / (annualSavings / 12));
  const productivityGains = (automatedGradingPercent + planningEfficiency) / 2;
  const studentOutcomeImprovement = 20; // Estimated improvement from teacher efficiency
  const costPerStudent = annualSavings / (teacherCount * avgClassSize);

  return {
    annualSavings: Math.round(annualSavings),
    threeYearROI: Math.round(threeYearROI),
    paybackPeriod,
    productivityGains: Math.round(productivityGains),
    studentOutcomeImprovement,
    costPerStudent: Math.round(costPerStudent),
    breakdownByCategory: {
      'Grading Automation': Math.round(gradingTimeSavings),
      'Planning Efficiency': Math.round(planningTimeSavings),
      'Professional Development': Math.round(annualSavings * 0.15),
    },
  };
}

// Cost Savings ROI Calculation
function calculateCostSavingsROI(inputs: ROIInputs): ROIResults {
  const {
    annualTechnologyBudget = 100000,
    textbookCostPerStudent = 500,
    professionalDevelopmentCost = 25000,
    digitalResourceSavings = 50,
    operationalSavings = 30,
  } = inputs;

  const studentCount = 500; // Estimated student count

  // Calculate digital resource savings
  const textbookSavings = studentCount * textbookCostPerStudent * (digitalResourceSavings / 100);
  const technologySavings = annualTechnologyBudget * (operationalSavings / 100);
  const pdSavings = professionalDevelopmentCost * 0.3; // 30% savings through digital PD

  // Total annual savings
  const annualSavings = textbookSavings + technologySavings + pdSavings;

  // Implementation cost
  const implementationCost = annualTechnologyBudget * 0.5; // 50% of annual budget

  // Calculate ROI metrics
  const threeYearROI = ((annualSavings * 3 - implementationCost) / implementationCost) * 100;
  const paybackPeriod = Math.ceil(implementationCost / (annualSavings / 12));
  const productivityGains = operationalSavings;
  const studentOutcomeImprovement = 12; // Estimated improvement from better resources
  const costPerStudent = annualSavings / studentCount;

  return {
    annualSavings: Math.round(annualSavings),
    threeYearROI: Math.round(threeYearROI),
    paybackPeriod,
    productivityGains,
    studentOutcomeImprovement,
    costPerStudent: Math.round(costPerStudent),
    breakdownByCategory: {
      'Digital Resource Savings': Math.round(textbookSavings),
      'Technology Optimization': Math.round(technologySavings),
      'Professional Development': Math.round(pdSavings),
      'Operational Efficiency': Math.round(annualSavings * 0.2),
    },
  };
}

// Learning Outcomes ROI Calculation
function calculateLearningOutcomesROI(inputs: ROIInputs): ROIResults {
  const {
    currentTestScores = 75,
    currentEngagement = 65,
    studentCount = 300,
    targetImprovement = 20,
    engagementIncrease = 35,
  } = inputs;

  // Calculate outcome improvement value
  const scoreImprovement = targetImprovement / 100;
  const engagementImprovement = engagementIncrease / 100;

  // Value per improvement point (estimated)
  const scoreValue = studentCount * scoreImprovement * 800; // $800 per percentage point
  const engagementValue = studentCount * engagementImprovement * 600; // $600 per engagement point

  // Reduced intervention costs
  const interventionSavings = studentCount * 0.2 * 1500; // 20% fewer students need intervention at $1500 each

  // Total annual savings
  const annualSavings = scoreValue + engagementValue + interventionSavings;

  // Implementation cost
  const implementationCost = studentCount * 100; // $100 per student

  // Calculate ROI metrics
  const threeYearROI = ((annualSavings * 3 - implementationCost) / implementationCost) * 100;
  const paybackPeriod = Math.ceil(implementationCost / (annualSavings / 12));
  const productivityGains = 25; // Teacher efficiency from better outcomes
  const studentOutcomeImprovement = targetImprovement;
  const costPerStudent = annualSavings / studentCount;

  return {
    annualSavings: Math.round(annualSavings),
    threeYearROI: Math.round(threeYearROI),
    paybackPeriod,
    productivityGains,
    studentOutcomeImprovement,
    costPerStudent: Math.round(costPerStudent),
    breakdownByCategory: {
      'Academic Achievement': Math.round(scoreValue),
      'Student Engagement': Math.round(engagementValue),
      'Intervention Reduction': Math.round(interventionSavings),
      'Long-term Value': Math.round(annualSavings * 0.25),
    },
  };
}

// Technology Investment ROI Calculation
function calculateTechnologyInvestmentROI(inputs: ROIInputs): ROIResults {
  const {
    implementationCost = 150000,
    annualLicenseCost = 50000,
    trainingCost = 20000,
    efficiencyGains = 45,
    costReductions = 35,
  } = inputs;

  const teacherCount = 25;
  const studentCount = 600;
  const averageTeacherSalary = 55000;

  // Calculate efficiency value
  const efficiencyValue = teacherCount * averageTeacherSalary * (efficiencyGains / 100) * 0.3; // 30% of salary equivalent

  // Calculate cost reduction value
  const operationalCosts = annualLicenseCost * 3; // Estimate operational costs
  const costReductionValue = operationalCosts * (costReductions / 100);

  // Calculate student outcome value
  const studentValue = studentCount * 200; // $200 per student in improved outcomes

  // Total annual savings
  const annualSavings = efficiencyValue + costReductionValue + studentValue;

  // Total implementation cost
  const totalImplementationCost = implementationCost + trainingCost + annualLicenseCost;

  // Calculate ROI metrics
  const threeYearROI = ((annualSavings * 3 - totalImplementationCost) / totalImplementationCost) * 100;
  const paybackPeriod = Math.ceil(totalImplementationCost / (annualSavings / 12));
  const productivityGains = efficiencyGains;
  const studentOutcomeImprovement = 18; // Estimated improvement from technology
  const costPerStudent = annualSavings / studentCount;

  return {
    annualSavings: Math.round(annualSavings),
    threeYearROI: Math.round(threeYearROI),
    paybackPeriod,
    productivityGains,
    studentOutcomeImprovement,
    costPerStudent: Math.round(costPerStudent),
    breakdownByCategory: {
      'Teacher Efficiency': Math.round(efficiencyValue),
      'Operational Cost Reduction': Math.round(costReductionValue),
      'Student Outcome Value': Math.round(studentValue),
      'Technology Optimization': Math.round(annualSavings * 0.2),
    },
  };
}
